import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, LogOut, CreditCard, History, LayoutDashboard, Loader2, Sparkles, Heart,
  Menu, X, Gift, Settings, Crown, Video, Hash, Repeat, Code, User, Flame, Microscope, Building2, Swords,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ThemeToggle } from "../contexts/ThemeContext";
import { CreditWarning } from "./CreditWarning";

const BASE_ITEMS = [
  { p: "/dashboard", l: "Dashboard", i: LayoutDashboard },
  { p: "/clone", l: "My Clone", i: User },
  { p: "/trends", l: "Trends", i: Flame },
  { p: "/roast", l: "Roast", i: Flame },
  { p: "/negative", l: "Negative", i: Swords },
  { p: "/autopsy", l: "Autopsy", i: Microscope },
  { p: "/generate", l: "Generate", i: Sparkles },
  { p: "/video", l: "Video", i: Video },
  { p: "/business/ads", l: "Business Ads", i: Building2 },
  { p: "/business/team", l: "Team", i: Building2 },
  { p: "/social", l: "Social", i: Hash },
  { p: "/history", l: "History", i: History },
  { p: "/favorites", l: "Favorites", i: Heart },
  { p: "/referrals", l: "Referrals", i: Gift },
  { p: "/subscription", l: "Plans", i: Repeat },
  { p: "/pricing", l: "Credits", i: CreditCard },
  { p: "/founders", l: "Lifetime", i: Crown },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mob, setMob] = useState(false);

  const bottomItems = [
    ...(user?.role === "admin" ? [{ p: "/admin", l: "Admin", i: Crown }] : []),
    { p: "/developer", l: "Developer", i: Code },
    { p: "/settings", l: "Settings", i: Settings },
  ];

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    nav("/");
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950" data-testid="app-shell">
      <nav className="fixed top-0 w-full backdrop-blur-xl bg-white/80 dark:bg-zinc-950/80 border-b border-zinc-200 dark:border-zinc-800 z-50" data-testid="app-nav">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => nav("/dashboard")}>
              <Zap className="w-5 h-5 text-brand" strokeWidth={2.5} />
              <span className="font-black text-lg tracking-tighter text-zinc-950 dark:text-white font-heading">CopyForge</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              {BASE_ITEMS.map(({ p, l, i: Icon }) => (
                <button
                  key={p}
                  data-testid={`nav-${l.toLowerCase()}`}
                  onClick={() => nav(p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-all ${loc.pathname === p ? "text-brand bg-brand/5" : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"}`}
                >
                  <Icon className="w-4 h-4" />
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {bottomItems.map(({ p, l, i: Icon }) => (
              <button
                key={p}
                data-testid={`nav-${l.toLowerCase()}`}
                onClick={() => nav(p)}
                className={`hidden md:flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium transition-all ${loc.pathname === p ? "text-brand" : "text-zinc-400 hover:text-zinc-700 dark:hover:text-white"}`}
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
            <ThemeToggle />
            <button onClick={() => nav("/pricing")} className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-700" data-testid="credit-display">
              <Zap className="w-4 h-4 text-brand" />
              <span className="text-sm font-bold text-zinc-900 dark:text-white" data-testid="credit-count">{user?.credits ?? 0}</span>
              <span className="text-xs text-zinc-500 hidden sm:inline">cr</span>
            </button>
            <button data-testid="logout-btn" onClick={handleLogout} className="text-zinc-400 hover:text-zinc-900 dark:hover:text-white hidden md:block">
              <LogOut className="w-5 h-5" />
            </button>
            <button data-testid="mobile-menu-btn" onClick={() => setMob(!mob)} className="md:hidden text-zinc-600 dark:text-zinc-400">
              {mob ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mob && (
          <div className="md:hidden border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-4 py-3 space-y-1 animate-fade-in" data-testid="mobile-menu">
            {[...BASE_ITEMS, ...bottomItems].map(({ p, l, i: Icon }) => (
              <button
                key={p}
                onClick={() => { nav(p); setMob(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium ${loc.pathname === p ? "text-brand bg-brand/5" : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900"}`}
              >
                <Icon className="w-4 h-4" />
                {l}
              </button>
            ))}
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-950">
              <LogOut className="w-4 h-4" /> Log Out
            </button>
          </div>
        )}
      </nav>
      <main className="pt-14">
        <CreditWarning />
        <div className="page-enter">{children}</div>
      </main>
    </div>
  );
}

export function PageLoader() {
  return (
    <AppShell>
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    </AppShell>
  );
}
